/**
 * Script Loader Utility
 * 
 * Provides safe, deferred loading of third-party scripts with:
 * - Single injection (no duplicates)
 * - Async/defer support
 * - Conditional loading
 * - Callbacks for onload/onerror
 * - Production-only loading option
 */

// Track loaded scripts to prevent duplicates
const loadedScripts = new Set();
const loadingScripts = new Map();

/**
 * Load a script dynamically
 * @param {string} src - Script URL
 * @param {Object} options - Loading options
 * @returns {Promise} - Resolves when script is loaded
 */
export function loadScript(src, options = {}) {
  const {
    async = true,
    defer = false,
    crossOrigin = 'anonymous',
    onLoad = null,
    onError = null,
    id = null,
    productionOnly = false,
    attributes = {}
  } = options;

  // Skip in development if productionOnly
  if (productionOnly && process.env.NODE_ENV === 'development') {
    return Promise.resolve({ skipped: true, reason: 'development' });
  }

  // Return existing promise if script is currently loading
  if (loadingScripts.has(src)) {
    return loadingScripts.get(src);
  }

  // Return resolved promise if script is already loaded
  if (loadedScripts.has(src)) {
    return Promise.resolve({ loaded: true, cached: true });
  }

  const promise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = async;
    script.defer = defer;
    
    if (crossOrigin) {
      script.crossOrigin = crossOrigin;
    }
    
    if (id) {
      script.id = id;
    }

    // Apply additional attributes
    Object.entries(attributes).forEach(([key, value]) => {
      script.setAttribute(key, value);
    });

    script.onload = () => {
      loadedScripts.add(src);
      loadingScripts.delete(src);
      if (onLoad) onLoad();
      resolve({ loaded: true });
    };

    script.onerror = (error) => {
      loadingScripts.delete(src);
      if (onError) onError(error);
      reject(error);
    };

    document.head.appendChild(script);
  });

  loadingScripts.set(src, promise);
  return promise;
}

/**
 * Load script after idle time or timeout
 * @param {string} src - Script URL
 * @param {Object} options - Loading options
 * @param {number} timeout - Fallback timeout in ms
 */
export function loadScriptOnIdle(src, options = {}, timeout = 3000) {
  return new Promise((resolve) => {
    const load = () => loadScript(src, options).then(resolve);

    if ('requestIdleCallback' in window) {
      requestIdleCallback(load, { timeout });
    } else {
      setTimeout(load, timeout);
    }
  });
}

/**
 * Load script after user interaction
 * @param {string} src - Script URL
 * @param {Object} options - Loading options
 */
export function loadScriptOnInteraction(src, options = {}) {
  return new Promise((resolve) => {
    const events = ['mousedown', 'touchstart', 'keydown', 'scroll'];
    
    const load = () => {
      events.forEach(event => {
        document.removeEventListener(event, load, { passive: true });
      });
      loadScript(src, options).then(resolve);
    };

    events.forEach(event => {
      document.addEventListener(event, load, { passive: true, once: true });
    });
  });
}

/**
 * Load script when element becomes visible
 * @param {Element} element - Target element
 * @param {string} src - Script URL
 * @param {Object} options - Loading options
 */
export function loadScriptOnVisible(element, src, options = {}) {
  return new Promise((resolve) => {
    if (!element) {
      loadScript(src, options).then(resolve);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          loadScript(src, options).then(resolve);
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(element);
  });
}

/**
 * Check if a script is already loaded
 * @param {string} src - Script URL
 */
export function isScriptLoaded(src) {
  return loadedScripts.has(src);
}

/**
 * Preconnect to a domain for faster subsequent requests
 * @param {string} origin - Origin URL
 */
export function preconnect(origin) {
  if (document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) {
    return;
  }
  
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = origin;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
}

export default {
  loadScript,
  loadScriptOnIdle,
  loadScriptOnInteraction,
  loadScriptOnVisible,
  isScriptLoaded,
  preconnect
};
